import { useEffect } from "react";
import { useLanguage } from "@/lib/i18n";
import { translations } from "@/lib/translations";
import {
  SITE_URL,
  applyPageSeo,
  breadcrumbLd,
  htmlLangOf,
  ogDefaultFor,
  upsertJsonLd,
  upsertMeta,
} from "@/lib/seo";
import {
  DEMO_DURATION,
  DEMO_PATH_EN,
  DEMO_PATH_FR,
  DEMO_POSTER_URL,
  DEMO_UPLOAD_DATE,
  DEMO_VIDEO_URL,
  demoSeo,
} from "./demo-seo-data";

/**
 * Runtime <head> for /demo. The same tags are prerendered at build time
 * (seo-head.ts) with matching data-seo-id values, so hydration replaces them
 * instead of duplicating them.
 */
export function DemoSEO() {
  const { language } = useLanguage();

  useEffect(() => {
    const isFr = language === "fr";
    const path = isFr ? DEMO_PATH_FR : DEMO_PATH_EN;
    const url = `${SITE_URL}${path}`;
    const seo = demoSeo(language);
    const t = translations[language].demo;

    applyPageSeo({
      lang: language,
      title: seo.title,
      description: seo.description,
      path,
      frPath: DEMO_PATH_FR,
      enPath: DEMO_PATH_EN,
      ogImage: ogDefaultFor(language),
    });

    // Video-specific Open Graph / Twitter tags so the clip itself can play in
    // social and press previews.
    upsertMeta("property", "og:video", DEMO_VIDEO_URL);
    upsertMeta("property", "og:video:secure_url", DEMO_VIDEO_URL);
    upsertMeta("property", "og:video:type", "video/mp4");
    upsertMeta("property", "og:video:width", "1080");
    upsertMeta("property", "og:video:height", "1920");
    upsertMeta("name", "twitter:player:stream", DEMO_VIDEO_URL);

    upsertJsonLd("demo-video", {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      "@id": `${url}#video`,
      name: t.title,
      description: t.subtitle,
      inLanguage: htmlLangOf(language),
      thumbnailUrl: [DEMO_POSTER_URL],
      uploadDate: DEMO_UPLOAD_DATE,
      duration: DEMO_DURATION,
      contentUrl: DEMO_VIDEO_URL,
      embedUrl: url,
      width: 1080,
      height: 1920,
      isFamilyFriendly: true,
      publisher: { "@id": `${SITE_URL}#organization` },
      about: { "@id": `${SITE_URL}#app` },
    });

    upsertJsonLd(
      "demo-breadcrumb",
      breadcrumbLd([
        { name: isFr ? "Accueil" : "Home", url: isFr ? `${SITE_URL}/` : `${SITE_URL}/en` },
        { name: isFr ? "Démo" : "Demo", url },
      ]),
    );
  }, [language]);

  return null;
}
